/**
 * Bottle amounts in ounces or millilitres. Babia stores every bottle in US
 * fluid ounces (`entry.amount_oz`, four decimals), and each household picks
 * the unit its parents think in (`household.bottle_unit`). The app, the
 * report, and the MCP tools keep their sums in stored ounces and convert at
 * the edge with these helpers, so a 90 ml bottle reads 90 ml everywhere and a
 * fortnight of them adds up to the millilitres the parents typed, while a
 * household in ounces never sees a decimal it didn't enter.
 */

export const BOTTLE_UNITS = ["oz", "ml"] as const;
export type BottleUnit = (typeof BOTTLE_UNITS)[number];

/** Millilitres in a US fluid ounce. */
export const ML_PER_OZ = 29.5735;

/** What an amount in each unit is called. */
export const BOTTLE_UNIT_NAME: Record<BottleUnit, string> = {
  oz: "ounces",
  ml: "millilitres",
};

/** The same, to start a heading: "Ounces a day". */
export const BOTTLE_UNIT_TITLE: Record<BottleUnit, string> = {
  oz: "Ounces",
  ml: "Millilitres",
};

/** The unit as a choice to pick, in Setup and Settings. */
export const BOTTLE_UNIT_LABEL: Record<BottleUnit, string> = {
  oz: "Ounces (oz)",
  ml: "Millilitres (ml)",
};

/** What the bottle sheet's – and + change, and the grid the usual bottle snaps to. */
export const BOTTLE_STEP: Record<BottleUnit, number> = { oz: 0.5, ml: 10 };

/** The smallest and largest bottle the sheet takes, in each unit. */
export const BOTTLE_LIMITS: Record<BottleUnit, { min: number; max: number }> = {
  oz: { min: 0.5, max: 20 },
  ml: { min: 10, max: 590 },
};

/** The grid preset sizes lie on: half ounces, or whole millilitres. */
export const PRESET_GRID: Record<BottleUnit, number> = { oz: 0.5, ml: 1 };

/** The preset chips a household starts with, and gets again when it switches units. */
export const BOTTLE_PRESETS: Record<BottleUnit, readonly number[]> = {
  oz: [2, 2.5, 3, 4],
  ml: [60, 90, 120, 150],
};

/** The usual bottle before any is logged. */
export const USUAL_BOTTLE: Record<BottleUnit, number> = { oz: 3, ml: 90 };

/**
 * Bottle sizes that count as a first, in each unit: the first 4 oz bottle,
 * or the first 120 ml bottle. Each list is whole multiples of its unit's
 * `BOTTLE_FIRST_STEP`, so a bottle's bucket is `floor(amount / step)`.
 */
export const BOTTLE_FIRSTS: Record<BottleUnit, readonly number[]> = {
  oz: [2, 3, 4, 5, 6, 7, 8],
  ml: [60, 90, 120, 150, 180, 210, 240],
};
export const BOTTLE_FIRST_STEP: Record<BottleUnit, number> = { oz: 1, ml: 30 };

/** What a household's bottles add up to: gallons of ounces, litres of millilitres. */
export const VOLUME_UNIT = { oz: "gallons", ml: "litres" } as const;
export type VolumeUnit = (typeof VOLUME_UNIT)[BottleUnit];
/** Ounces in a gallon, millilitres in a litre. */
export const PER_VOLUME: Record<BottleUnit, number> = { oz: 128, ml: 1000 };
/** Gallons or litres of bottles, all added up, that count as a milestone. */
export const VOLUME_FIRSTS: Record<BottleUnit, readonly number[]> = {
  oz: [1, 5, 10, 25, 50, 100],
  ml: [5, 10, 25, 50, 100, 250],
};

/** Stored ounces rounded to the column's four decimals; sums of them stay exact to a millilitre. */
export function roundOz(oz: number): number {
  return Math.round(Number((oz * 10_000).toPrecision(12))) / 10_000;
}

/** An amount in `unit` rounded as it is shown: ounces to a tenth, millilitres whole. */
export function roundAmount(amount: number, unit: BottleUnit): number {
  return unit === "ml"
    ? Math.round(Number(amount.toPrecision(12)))
    : Math.round(Number((amount * 10).toPrecision(12))) / 10;
}

/** Stored ounces from an amount in `unit`. */
export function toOz(amount: number, unit: BottleUnit): number {
  return roundOz(unit === "ml" ? amount / ML_PER_OZ : amount);
}

/** The amount in `unit` that stored ounces show as. */
export function fromOz(oz: number, unit: BottleUnit): number {
  return roundAmount(unit === "ml" ? oz * ML_PER_OZ : oz, unit);
}

/** "3.5 oz", "90 ml". */
export function amountText(amount: number, unit: BottleUnit): string {
  return `${roundAmount(amount, unit)} ${unit}`;
}

/** Stored ounces as the household reads them: "3.5 oz", "90 ml". */
export function formatAmount(oz: number, unit: BottleUnit): string {
  return amountText(fromOz(oz, unit), unit);
}

/** An amount in `unit` snapped to its step: a size a parent pours (3.25 oz → 3.5, 122 ml → 120). */
export function roundToStep(amount: number, unit: BottleUnit): number {
  const step = BOTTLE_STEP[unit];
  return roundAmount(Math.round(amount / step) * step, unit);
}

/** Stored ounces snapped to the unit's step, still in ounces. */
export function snapOz(oz: number, unit: BottleUnit): number {
  return toOz(roundToStep(unit === "ml" ? oz * ML_PER_OZ : oz, unit), unit);
}

/** Why `amount` can't be a bottle in `unit`, or null. */
export function amountProblem(amount: number, unit: BottleUnit): string | null {
  const { min, max } = BOTTLE_LIMITS[unit];
  if (!Number.isFinite(amount) || amount < min || amount > max)
    return `a bottle must be between ${min} and ${max} ${unit}`;
  return null;
}

/** Why `amount` can't be a preset size in `unit` (off the unit's grid too), or null. */
export function presetProblem(amount: number, unit: BottleUnit): string | null {
  const problem = amountProblem(amount, unit);
  if (problem) return problem;
  const grid = PRESET_GRID[unit];
  if (Math.abs(Math.round(amount / grid) * grid - amount) > 1e-9)
    return unit === "oz"
      ? "bottle sizes are in half-ounce steps"
      : "bottle sizes are whole millilitres";
  return null;
}

/** The server's range for a stored amount, whichever unit it came in. */
export const MIN_BOTTLE_OZ = Math.min(...BOTTLE_UNITS.map((u) => toOz(BOTTLE_LIMITS[u].min, u)));
export const MAX_BOTTLE_OZ = Math.max(...BOTTLE_UNITS.map((u) => toOz(BOTTLE_LIMITS[u].max, u)));
